// ** MUI Imports
import {
  Box,
  Typography,
  MenuItem,
  IconButton,
  Checkbox,
  FormLabel,
  Tooltip,
} from "@mui/material";
import "assets/CSS/Switch.css";

// ** Custom Component Imports
import CustomTextField from "components/mui/text-field";

// ** Icon Imports
import Icon from "components/icon";
import { GridActionsCellItem } from "@mui/x-data-grid";
import { formatUSCurrency } from "components/CurrencyFormat";
import { PercentageFormat } from "components/NumberFormats";


export const GridColumns = ({
  handleJobAssetEdit,
  handleJobAssetDelete,
  handleJobMaterialDelete,
  handleJobMaterialEdit,
  handleJobMiscCostDelete,
  handleJobMiscCostEdit,
  handleJobLaborDelete,
  handleJobLaborEdit,
  handleJobSubcontractorDelete,
  handleJobSubcontractorEdit,
  disableEdit = false,
}) => {

  //#region Material
  const columnsMaterial = [
    {
      flex: 0.1,
      field: "materialTypeName",
      minWidth: 160,
      headerName: "Material Type",
      renderCell: ({ row }) => {
        const { materialTypeName } = row;
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Typography>{materialTypeName}</Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "materialName",
      minWidth: 180,
      headerName: "Material Name",
      renderCell: ({ row }) => {
        const { materialName } = row;
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Typography>{materialName}</Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "unitOfMeasure",
      minWidth: 180,
      headerName: "Unit Of Measure",
      renderCell: ({ row }) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            height: "100%",
          }}
        >
          <Typography>{row.unitName || ''}{row.unitSymbol ? ` (${row.unitSymbol})` : ''}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "budgetedQuantity",
      minWidth: 120,
      headerName: "Quantity",
      renderCell: ({ row }) => {
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Typography>{row.budgetedQuantity}</Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "budgetedCostPerUnit",
      minWidth: 140,
      headerName: "Cost / Unit",
      renderCell: ({ row }) => {
        const { budgetedCostPerUnit } = row;
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Typography>{formatUSCurrency(budgetedCostPerUnit)}</Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "budgetedTotalCost",
      minWidth: 140,
      headerName: "Total Cost",
      renderCell: ({ row }) => {
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Typography>{formatUSCurrency(row.budgetedTotalCost)}</Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "budgetedPricePerUnit",
      minWidth: 160,
      headerName: "Price / Unit",
      renderCell: ({ row }) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            height: "100%",
          }}
        >
          <Typography>{formatUSCurrency(row.budgetedPricePerUnit)}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "budgetedTotalPrice",
      minWidth: 140,
      headerName: "Total Price",
      renderCell: ({ row }) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            height: "100%",
          }}
        >
          <Typography>{formatUSCurrency(row.budgetedTotalPrice)}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "budgetedMarkup",
      minWidth: 140,
      headerName: "Markup %",
      renderCell: ({ row }) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            height: "100%",
          }}
        >
          <Typography>{PercentageFormat(row.budgetedMarkup)}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "budgetedProfitPerUnit",
      minWidth: 160,
      headerName: "Profit / Unit",
      renderCell: ({ row }) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            height: "100%",
          }}
        >
          <Typography>{formatUSCurrency(row.budgetedProfitPerUnit)}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "budgetedTotalProfit",
      minWidth: 140,
      headerName: "Total Profit",
      renderCell: ({ row }) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            height: "100%",
          }}
        >
          <Typography>{formatUSCurrency(row.budgetedTotalProfit)}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "budgetedProfitMargin",
      minWidth: 180,
      headerName: "Profit Margin %",
      renderCell: ({ row }) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            height: "100%",
          }}
        >
          <Typography>{PercentageFormat(row.budgetedProfitMargin)}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "providedByName",
      minWidth: 160,
      headerName: "Provided By",
      renderCell: ({ row }) => {
        const { providedByName } = row;
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Typography>{providedByName}</Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: 'actions',
      headerName: 'Actions',
      minWidth: 120,
      maxWidth: 120,
      type: 'actions',
      getActions: ({ id , row}) => disableEdit ? [] : [        
          <GridActionsCellItem
            sx={{ transform: 'scale(1.5)', marginRight:3 }}  
            icon={<Icon icon="tabler:pencil" />}
            label="Edit"
            onClick={() =>  handleJobMaterialEdit(row)}
          />,       
          <GridActionsCellItem
            color="error"
            sx={{ transform: 'scale(1.5)' }}  
            icon={<Icon icon="tabler:trash" />}
            label="Delete"
            onClick={() =>  handleJobMaterialDelete(id)}
          />
      ],
    },
  ];
  
  const columnsActualMaterial = [
    {
      flex: 0.1,
      field: "itemType",
      minWidth: 170,
      headerName: "Material Type",
      renderCell: ({ row }) => {
        const { itemType } = row;
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Typography>{itemType}</Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "itemName",
      minWidth: 140,
      headerName: "Name",
      renderCell: ({ row }) => {
        const { itemName } = row;
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Typography>{itemName}</Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "unit",
      minWidth: 100,
      headerName: "Units",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{row.unitName || ''}{row.unitSymbol ? ` (${row.unitSymbol})` : ''}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "unitQuantity",
      minWidth: 180,
      headerName: "Actual Qty Used",
      renderCell: ({ row }) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            height: "100%",
          }}
        >
          <Typography>{row.unitQuantity}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "unitCost",
      minWidth: 180,
      headerName: "Actual Unit Cost",
      renderCell: ({ row }) => {
        const { unitCost } = row;
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Typography>{unitCost}</Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "totalCost",
      minWidth: 190,
      headerName: "Actual Total Cost",
      renderCell: ({ row }) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            height: "100%",
          }}
        >
          <Typography>{row.totalCost}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "variance",
      minWidth: 140,
      headerName: "Variance",
      renderCell: ({ row }) => {
        const { variance } = row;
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Typography color={typeof variance === "string" && variance.includes("+") ? "success" : "error"}>
              {variance}
            </Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "modifiedBy",
      minWidth: 180,
      headerName: "Modified By",
      renderCell: ({ row }) => {
        const { modifiedbyname } = row;
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Typography>{modifiedbyname}</Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: 'actions',
      headerName: 'Actions',
      minWidth: 120,
      maxWidth: 120,
      type: 'actions',
      getActions: ({ id, row }) => disableEdit ? [] : [
        <GridActionsCellItem
          sx={{ transform: 'scale(1.5)', marginRight:3 }}
          icon={<Icon icon="tabler:pencil" />}
          label="Edit"
          onClick={() => handleJobMaterialEdit(row)}
          key="edit"
        />,
        <GridActionsCellItem
          color="error"
          sx={{ transform: 'scale(1.5)' }}
          icon={<Icon icon="tabler:trash" />}
          label="Delete"
          onClick={() => handleJobMaterialDelete(id)}
          key="delete"
        />,
      ],
    },    
  ];
  
  const columnsActualvsBudgetMaterial = [
    {
      flex: 0.1,
      field: "itemName",
      minWidth: 180,
      headerName: "Material Name",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{row.itemName}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "unit",
      minWidth: 180,
      headerName: "Unit of Measure",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{row.unitName || ''}{row.unitSymbol ? ` (${row.unitSymbol})` : ''}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "budgetQuantity",
      minWidth: 200,
      headerName: "Budgeted Quantity",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{row.budgetQuantity}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "actualQuantity",
      minWidth: 180,
      headerName: "Actual Quantity",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{row.actualQuantity}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "budgetCost",
      minWidth: 220,
      headerName: "Budgeted Cost / Unit",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{formatUSCurrency(row.budgetCost)}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "actualCost",
      minWidth: 220,
      headerName: "Actual Cost / Unit",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{formatUSCurrency(row.actualCost)}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "budgetTotalCost",
      minWidth: 220,
      headerName: "Budgeted Total Cost",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{formatUSCurrency(row.budgetTotalCost)}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "actualTotalCost",
      minWidth: 190,
      headerName: "Actual Total Cost",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{formatUSCurrency(row.actualTotalCost)}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "costVariance",
      minWidth: 160,
      headerName: "Cost Variance",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{formatUSCurrency(row.costVariance)}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "costVariancePercentage",
      minWidth: 180,
      headerName: "Cost Variance %",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{row.costVariancePercentage || ''}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "budgetedPricePerUnit",
      minWidth: 220,
      headerName: "Budgeted Price / Unit",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{formatUSCurrency(row.budgetedPricePerUnit)}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "actualPricePerUnit",
      minWidth: 220,
      headerName: "Actual Price / Unit",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{formatUSCurrency(row.actualPricePerUnit)}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "budgetedTotalPrice",
      minWidth: 220,
      headerName: "Budgeted Total Price",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{formatUSCurrency(row.budgetedTotalPrice)}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "actualTotalPrice",
      minWidth: 220,
      headerName: "Actual Total Price",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{formatUSCurrency(row.actualTotalPrice)}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "priceVariance",
      minWidth: 180,
      headerName: "Price Variance",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{formatUSCurrency(row.priceVariance)}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "priceVariancePercentage",
      minWidth: 180,
      headerName: "Price Variance %",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{row.priceVariancePercentage}</Typography>
        </Box>
      ),
    },
  ];
  //#endregion Material

  //#region Labor
  const columnsLabor = [
    {
      flex: 0.1,
      field: "skillType",
      minWidth: 160,
      headerName: "Skill Type",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{row.skillType}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "employeeName",
      minWidth: 200,
      headerName: "Employee / Team",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{row.employeeName}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "budgetedCostPerHour",
      minWidth: 160,
      headerName: "Cost / Hour",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{formatUSCurrency(row.budgetedCostPerHour)}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "budgetedHours",
      minWidth: 180,
      headerName: "Budgeted Hours",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{row.budgetedHours}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "budgetedTotalCost",
      minWidth: 140,
      headerName: "Total Cost",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{formatUSCurrency(row.budgetedTotalCost)}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "budgetedPricePerHour",
      minWidth: 160,
      headerName: "Price / Hour",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{formatUSCurrency(row.budgetedPricePerHour)}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "budgetedTotalPrice",
      minWidth: 140,
      headerName: "Total Price",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{formatUSCurrency(row.budgetedTotalPrice)}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "markup",
      minWidth: 140,
      headerName: "Markup %",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{PercentageFormat(row.budgetedMarkup)}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "budgetedProfitPerHour",
      minWidth: 160,
      headerName: "Profit / Hour",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{formatUSCurrency(row.budgetedProfitPerHour)}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "budgetedTotalProfit",
      minWidth: 140,
      headerName: "Total Profit",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{formatUSCurrency(row.budgetedTotalProfit)}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "budgetedProfitMargin",
      minWidth: 180,
      headerName: "Profit Margin %",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{PercentageFormat((row.budgetedProfitMargin))}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "providedByName",
      minWidth: 160,
      headerName: "Provided By",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{row.providedByName}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "rentalOrOwned",
      minWidth: 180,
      headerName: "Rent / Own",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{row.rentalStatus === "Owned" ? "Own" : "Rent"}</Typography>
        </Box>      
      ),
    },
    {
      flex: 0.1,
      field: 'actions',
      headerName: 'Actions',
      minWidth: 120,
      maxWidth: 120,
      type: 'actions',
      getActions: ({ id , row}) => disableEdit ? [] : [        
          <GridActionsCellItem
            sx={{ transform: 'scale(1.5)', marginRight:3 }}  
            icon={<Icon icon="tabler:pencil" />}
            label="Edit"
            onClick={() =>  handleJobLaborEdit(row)}
          />,       
          <GridActionsCellItem
            color="error"
            sx={{ transform: 'scale(1.5)' }}  
            icon={<Icon icon="tabler:trash" />}
            label="Delete"
            onClick={() =>  handleJobLaborDelete(id)}
          />
      ],
    },
  ];
  
  const columnsActualLabor = [
    {
      flex: 0.1,
      field: "skillType",
      minWidth: 140,
      headerName: "Skill Type",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{row.skillType}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "employee",
      minWidth: 180,
      headerName: "Employee Name",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{row.employee}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "hourlyRate",
      minWidth: 200,
      headerName: "Actual Hourly Rate",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{row.hourlyRate}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "manHours",
      minWidth: 220,
      headerName: "Actual Hours Worked",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{row.manHours}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "totalCost",
      minWidth: 200,
      headerName: "Actual Total Cost",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{row.totalCost}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "varianceHrs",
      minWidth: 180,
      headerName: "Variance (Hrs)",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography color={typeof row.varianceHrs === 'string' && row.varianceHrs.includes("+") ? "success" : "error"}>
            {row.varianceHrs}
          </Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "varianceAmnt",
      minWidth: 180,
      headerName: "Variance ($)",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography color={typeof row.varianceAmnt === 'string' && row.varianceAmnt.includes("+") ? "success" : "error"}>
            {row.varianceAmnt}
          </Typography>
        </Box>
      ),
    },
     {
      flex: 0.1,
      field: "modifiedBy",
      minWidth: 180,
      headerName: "Modified By",
      renderCell: ({ row }) => {
        const { modifiedbyname } = row;
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Typography>{modifiedbyname}</Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: 'actions',
      headerName: 'Actions',
      minWidth: 120,
      maxWidth: 120,
      type: 'actions',
      getActions: ({ id, row }) => disableEdit ? [] : [
        <GridActionsCellItem
          sx={{ transform: 'scale(1.5)', marginRight:3 }}
          icon={<Icon icon="tabler:pencil" />}
          label="Edit"
          onClick={() => handleJobLaborEdit(row)}
          key="edit"
        />,
        <GridActionsCellItem
          color="error"
          sx={{ transform: 'scale(1.5)' }}
          icon={<Icon icon="tabler:trash" />}
          label="Delete"
          onClick={() => handleJobLaborDelete(id)}
          key="delete"
        />,
      ],
    },
  ];

  const columnsActualvsBudgetLabor = [
    {
      field: "employee",
      minWidth: 200,
      headerName: "Employee / Team",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{row.employee}</Typography>
        </Box>
      ),
    },
    {
      field: "skillType",
      minWidth: 140,
      headerName: "Skill Type",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{row.skillType}</Typography>
        </Box>
      ),
    },
    {
      field: "budgetManHours",
      minWidth: 180,
      headerName: "Budgeted Hours",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{row.budgetManHours}</Typography>
        </Box>
      ),
    },
    {
      field: "actualManHours",
      minWidth: 160,
      headerName: "Actual Hours",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{row.actualManHours}</Typography>
        </Box>
      ),
    },
    {
      field: "budgetCost",
      minWidth: 220,
      headerName: "Budgeted Cost / Hour",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{formatUSCurrency(row.budgetCost)}</Typography>
        </Box>
      ),
    },
    {
      field: "actualCost",
      minWidth: 200,
      headerName: "Actual Cost / Hour",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{formatUSCurrency(row.actualCost)}</Typography>
        </Box>
      ),
    },
    {
      field: "budgetTotalCost",
      minWidth: 220,
      headerName: "Budgeted Total Cost",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography color="text.primary">{formatUSCurrency(row.budgetTotalCost)}</Typography>
        </Box>
      ),
    },
    {
      field: "actualTotalCost",
      minWidth: 200,
      headerName: "Actual Total Cost",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography color="text.primary">{formatUSCurrency(row.actualTotalCost)}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "costVariance",
      minWidth: 180,
      headerName: "Cost Variance",
      renderCell: ({ row }) => {
        const value = typeof row.costVariance === 'number' && !isNaN(row.costVariance) ? row.costVariance : 0;
        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography color="text.primary">{formatUSCurrency(value)}</Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "costVariancePercentage",
      minWidth: 180,
      headerName: "Cost Variance %",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography color="text.primary">{row.costVariancePercentage}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "budgetedTotalPrice",
      minWidth: 220,
      headerName: "Budgeted Total Price",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography color="text.primary">{formatUSCurrency(row.budgetedTotalPrice)}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "actualTotalPrice",
      minWidth: 220,
      headerName: "Actual Total Price",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography color="text.primary">{formatUSCurrency(row.actualTotalPrice)}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "priceVariance",
      minWidth: 200,
      headerName: "Price Variance",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography color="text.primary">{formatUSCurrency(row.priceVariance)}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "priceVariancePercentage",
      minWidth: 200,
      headerName: "Price Variance %",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography color="text.primary">{row.priceVariancePercentage}</Typography>
        </Box>
      ),
    },
  ];
  //#endregion Labor
  
  //#region Asset
  const columnsAssets = [
    {
      flex: 0.1,
      field: "assetTypeName",
      minWidth: 150,
      headerName: "Asset Type",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{row.assetTypeName}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "assetName",
      minWidth: 150,
      headerName: "Asset Name",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{row.assetName}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "budgetedQuantity",
      minWidth: 120,
      headerName: "Quantity",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{row.budgetedQuantity}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "billingUnit",
      minWidth: 140,
      headerName: "Hour / Day",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{row.billingUnit}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "budgetedCostPerUnit",
      minWidth: 180,
      headerName: "Cost Per Asset",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{formatUSCurrency(row.budgetedCostPerUnit)}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "budgetedDuration",
      minWidth: 140,
      headerName: "Duration",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{row.budgetedDuration}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "budgetedTotalCost",
      minWidth: 140,
      headerName: "Total Cost",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{formatUSCurrency(row.budgetedTotalCost)}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "billToCustomer",
      minWidth: 200,
      headerName: "Bill To Customer",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{row.billToCustomer ? "Yes" : "No"}</Typography>
        </Box>      
      ),
    },
    {
      flex: 0.1,
      field: "budgetedPricePerUnit",
      minWidth: 200,
      headerName: "Price Per Asset",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{formatUSCurrency(row.budgetedPricePerUnit)}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "budgetedTotalPrice",
      minWidth: 160,
      headerName: "Total Price",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{formatUSCurrency(row.budgetedTotalPrice)}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "budgetedProfitPerUnit",
      minWidth: 190,
      headerName: "Profit Per Asset",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{formatUSCurrency(row.budgetedProfitPerUnit)}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "budgetedTotalProfit",
      minWidth: 140,
      headerName: "Total Profit",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{formatUSCurrency(row.budgetedTotalProfit)}</Typography>
        </Box>
      ),
    },
        {
      flex: 0.1,
      field: "budgetedMarkup",
      minWidth: 140,
      headerName: "Markup %",
      renderCell: ({ row }) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            height: "100%",
          }}
        >
          <Typography>{PercentageFormat(row.budgetedMarkup)}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "budgetedProfitMargin",
      minWidth: 180,
      headerName: "Profit Margin %",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{PercentageFormat(row.budgetedProfitMargin)}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "providedByName",
      minWidth: 160,
      headerName: "Provided By",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{row.providedByName}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "rentalOrOwned",
      minWidth: 180,
      headerName: "Rent / Own",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{row.rentalStatus === "Owned" ? "Own" : "Rent"}</Typography>
        </Box>      
      ),
    },
    {
      flex: 0.1,
      field: 'actions',
      headerName: 'Actions',
      minWidth: 120,
      maxWidth: 120,
      type: 'actions',
      getActions: ({ id , row}) => disableEdit ? [] : [        
          <GridActionsCellItem
            sx={{ transform: 'scale(1.5)', marginRight:3 }}  
            icon={<Icon icon="tabler:pencil" />}
            label="Edit"
            onClick={() =>  handleJobAssetEdit(row)}
          />,       
          <GridActionsCellItem
            color="error"
            sx={{ transform: 'scale(1.5)' }}  
            icon={<Icon icon="tabler:trash" />}
            label="Delete"
            onClick={() =>  handleJobAssetDelete(id)}
          />
      ],
    },
  ];
  
  const columnsAssetsDetail = [
    {
      field: "id",
      minWidth: 70,
      headerName: "SN",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography fontWeight={row.id === "summary" ? "bold" : "normal"}>
            {row.id === "summary" ? "Total:" : row.id}
          </Typography>
        </Box>
      ),
    },
    {
      field: "assetType",
      minWidth: 150,
      headerName: "Assets Type",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{row.assetType}</Typography>
        </Box>
      ),
    },
    {
      field: "assetName",
      minWidth: 150,
      headerName: "Assets Name",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{row.assetName}</Typography>
        </Box>
      ),
    },
    {
      field: "quantity",
      minWidth: 120,
      headerName: "Qty",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{row.quantity}</Typography>
        </Box>
      ),
    },
    {
      field: "hourOrDay",
      minWidth: 140,
      headerName: "Hour/Day",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{row.hourOrDay}</Typography>
        </Box>
      ),
    },
    {
      field: "markup",
      minWidth: 140,
      headerName: "Markup (%)",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{row.budgetedMarkup}</Typography>
        </Box>
      ),
    },
    {
      field: "hourlyOrDailyRate",
      minWidth: 180,
      headerName: "Hourly/Day Rate",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{row.hourlyOrDailyRate}</Typography>
        </Box>
      ),
    },
    {
      field: "transportationCost",
      minWidth: 200,
      headerName: "Transportation Cost",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{row.transportationCost}</Typography>
        </Box>
      ),
    },
    {
      field: "setupUnsetupTime",
      minWidth: 200,
      headerName: "Setup/Unsetup Time",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{row.setupUnsetupTime}</Typography>
        </Box>
      ),
    },
    {
      field: "setupUnsetupCost",
      minWidth: 200,
      headerName: "Setup/Unsetup Cost",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{row.setupUnsetupCost}</Typography>
        </Box>
      ),
    },
    {
      field: "contingencyCost",
      minWidth: 190,
      headerName: "Contingency Cost",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{row.contingencyCost}</Typography>
        </Box>
      ),
    },
    {
      field: "totalProfit",
      minWidth: 140,
      headerName: "Total Profit",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{row.totalProfit}</Typography>
        </Box>
      ),
    },
    {
      field: "totalCost",
      minWidth: 140,
      headerName: "Total Cost",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{row.totalCost}</Typography>
        </Box>
      ),
    },
    {
      field: "totalPrice",
      minWidth: 140,
      headerName: "Total Price",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{row.totalPrice}</Typography>
        </Box>
      ),
    },
    {
      field: "providedBy",
      minWidth: 140,
      headerName: "Provided By",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{row.providedBy}</Typography>
        </Box>
      ),
    },
    {
      field: "rentalOrOwned",
      minWidth: 170,
      headerName: "Rental/Owned",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{row.rentalOrOwned}</Typography>
        </Box>
      ),
    },
    {
      field: "actions",
      minWidth: 120,
      headerName: "Actions",
      renderCell: ({ row }) => {
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            {row.id !== "summary" && (
              <IconButton>
                <Icon icon="tabler:trash" />
              </IconButton>
            )}
          </Box>
        );
      },
    },
  ];
  
  const columnsActualAssets = [
    {
      flex: 0.1,
      field: "assetTypeName",
      minWidth: 150,
      headerName: "Asset Type",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{row.assetTypeName}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "assetName",
      minWidth: 150,
      headerName: "Asset Name",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{row.assetName}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "quantity",
      minWidth: 120,
      headerName: "Actual Quantity",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{row.quantity}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "duration",
      minWidth: 120,
      headerName: "Actual Duration",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{row.duration}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "unitCost",
      minWidth: 120,
      headerName: "Unit Cost",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{row.unitCost}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "totalCost",
      minWidth: 150,
      headerName: "Total Cost",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{row.totalCost}</Typography>
        </Box>
      ),
    },
     {
      flex: 0.1,
      field: "modifiedBy",
      minWidth: 180,
      headerName: "Modified By",
      renderCell: ({ row }) => {
        const { modifiedbyname } = row;
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Typography>{modifiedbyname}</Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: 'actions',
      headerName: 'Actions',
      minWidth: 120,
      maxWidth: 120,
      type: 'actions',
      getActions: ({ id, row }) => disableEdit ? [] : [
        <GridActionsCellItem
          sx={{ transform: 'scale(1.5)', marginRight:3 }}
          icon={<Icon icon="tabler:pencil" />}
          label="Edit"
          onClick={() => handleJobAssetEdit(row)}
          key="edit"
        />,
        <GridActionsCellItem
          color="error"
          sx={{ transform: 'scale(1.5)' }}
          icon={<Icon icon="tabler:trash" />}
          label="Delete"
          onClick={() => handleJobAssetDelete(id)}
          key="delete"
        />,
      ],
    },
  ];
  
  const columnsActualvsBudgetAssets = [
    {
      field: "assetName",
      minWidth: 150,
      headerName: "Asset Name",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{row.assetName}</Typography>
        </Box>
      ),
    },
    {
      field: "assetType",
      minWidth: 150,
      headerName: "Asset Type",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{row.assetType}</Typography>
        </Box>
      ),
    },
    {
      field: "budgetUsageDuration",
      minWidth: 260,
      headerName: "Budgeted Duration (Days)",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{row.budgetUsageDuration}</Typography>
        </Box>
      ),
    },
    {
      field: "actualUsageDuration",
      minWidth: 280,
      headerName: "Actual Duration (Hours/Days)",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{row.actualUsageDuration}</Typography>
        </Box>
      ),
    },
    {
      field: "budgetCost",
      minWidth: 220,
      headerName: "Budgeted Cost / Day",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{formatUSCurrency(row.budgetCost)}</Typography>
        </Box>
      ),
    },
    {
      field: "actualCost",
      minWidth: 200,
      headerName: "Actual Cost / Day",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{formatUSCurrency(row.actualCost)}</Typography>
        </Box>
      ),
    },
    {
      field: "budgetTotalCost",
      minWidth: 240,
      headerName: "Budgeted Total Cost",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{formatUSCurrency(row.budgetTotalCost)}</Typography>
        </Box>
      ),
    },
    {
      field: "actualTotalCost",
      minWidth: 200,
      headerName: "Actual Total Cost",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{formatUSCurrency(row.actualTotalCost)}</Typography>
        </Box>
      ),
    },
    {
      field: "costVariance",
      minWidth: 180,
      headerName: "Cost Variance",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{formatUSCurrency(row.costVariance)}</Typography>
        </Box>
      ),
    },
    {
      field: "costVariancePercentage",
      minWidth: 180,
      headerName: "Cost Variance %",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{row.costVariancePercentage || ''}</Typography>
        </Box>
      ),
    },
    {
      field: "budgetedPricePerUnit",
      minWidth: 220,
      headerName: "Budgeted Price / Day",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{formatUSCurrency(row.budgetedPricePerUnit)}</Typography>
        </Box>
      ),
    },
    {
      field: "actualPricePerUnit",
      minWidth: 220,
      headerName: "Actual Price / Day",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{formatUSCurrency(row.actualPricePerUnit)}</Typography>
        </Box>
      ),
    },
    {
      field: "budgetedTotalPrice",
      minWidth: 220,
      headerName: "Budgeted Total Price",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography color="text.primary">{formatUSCurrency(row.budgetedTotalPrice)}</Typography>
        </Box>
      ),
    },
    {
      field: "actualTotalPrice",
      minWidth: 220,
      headerName: "Actual Total Price",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography color="text.primary">{formatUSCurrency(row.actualTotalPrice)}</Typography>
        </Box>
      ),
    },
    {
      field: "priceVariance",
      minWidth: 200,
      headerName: "Price Variance",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography color="text.primary">{formatUSCurrency(row.priceVariance)}</Typography>
        </Box>
      ),
    },
    {
      field: "priceVariancePercentage",
      minWidth: 200,
      headerName: "Price Variance %",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography color="text.primary">{row.priceVariancePercentage}</Typography>
        </Box>
      ),
    },
  ];
  //#endregion Asset
  
  //#region Other Cost
   const columnsOtherCost = [
    {
      flex: 0.1,
      field: "costName",
      minWidth: 180,
      headerName: "Cost Name",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{row.costName}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "budgetedCostAmount",
      minWidth: 140,
      headerName: "Cost Amount",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{formatUSCurrency(row.budgetedCostAmount)}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "billToCustomer",
      minWidth: 140,
      headerName: "Bill To Customer",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{row.billToCustomer ? "Yes" : "No"}</Typography>                      
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "budgetedCustomerPrice",
      minWidth: 140,
      headerName: "Customer Price",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{formatUSCurrency(row.budgetedCustomerPrice)}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "budgetedProfit",
      minWidth: 240,
      headerName: "Total Profit",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{formatUSCurrency(row.budgetedProfit)}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "budgetedProfitMargin",
      minWidth: 240,
      headerName: "Profit Margin %",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{PercentageFormat(row.budgetedProfitMargin)}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: 'actions',
      headerName: 'Actions',
      minWidth: 120,
      maxWidth: 120,
      type: 'actions',
      getActions: ({ id , row}) => disableEdit ? [] : [        
          <GridActionsCellItem
            sx={{ transform: 'scale(1.5)', marginRight:3 }}  
            icon={<Icon icon="tabler:pencil" />}
            label="Edit"
            onClick={() =>  handleJobMiscCostEdit(row)}
          />,       
          <GridActionsCellItem
            color="error"
            sx={{ transform: 'scale(1.5)' }}  
            icon={<Icon icon="tabler:trash" />}
            label="Delete"
            onClick={() =>  handleJobMiscCostDelete(id)}
          />
      ],
    },
  ];
  
  const columnsActualOtherCost = [
    {
      flex: 0.1,
      field: "costDescription",
      minWidth: 180,
      headerName: "Cost Description",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{row.costDescription}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "unitCost",
      minWidth: 180,
      headerName: "Actual Unit Cost",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{row.unitCost}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "totalCost",
      minWidth: 200,
      headerName: "Actual Total Cost",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{row.totalCost}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "variance",
      minWidth: 120,
      headerName: "Variance ($)",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography color={typeof row.variance === 'string' && row.variance.includes("+") ? "success" : "error"}>
            {row.variance}
          </Typography>
        </Box>
      ),
    },
     {
      flex: 0.1,
      field: "modifiedBy",
      minWidth: 180,
      headerName: "Modified By",
      renderCell: ({ row }) => {
        const { modifiedbyname } = row;
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Typography>{modifiedbyname}</Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: 'actions',
      headerName: 'Actions',
      minWidth: 120,
      maxWidth: 120,
      type: 'actions',
      getActions: ({ id, row }) => disableEdit ? [] : [
        <GridActionsCellItem
          sx={{ transform: 'scale(1.5)', marginRight:3 }}
          icon={<Icon icon="tabler:pencil" />}
          label="Edit"
          onClick={() => handleJobMiscCostEdit(row)}
          key="edit"
        />,
        <GridActionsCellItem
          color="error"
          sx={{ transform: 'scale(1.5)' }}
          icon={<Icon icon="tabler:trash" />}
          label="Delete"
          onClick={() => handleJobMiscCostDelete(id)}
          key="delete"
        />,
      ],
    },
  ];
  
   const columnsActualvsBudgetOtherCost = [
    {
      field: "costName",
      minWidth: 180,
      headerName: "Other Cost Name",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{row.costName}</Typography>
        </Box>
      ),
    },
    {
      field: "budgetCost",
      minWidth: 180,
      headerName: "Budgeted Cost",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{formatUSCurrency(row.budgetCost)}</Typography>
        </Box>
      ),
    },
    {
      field: "actualCost",
      minWidth: 180,
      headerName: "Actual Cost",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{formatUSCurrency(row.actualCost)}</Typography>
        </Box>
      ),
    },
    {
      field: "budgetTotalCost",
      minWidth: 220,
      headerName: "Budgeted Total Cost",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography color="text.primary">{formatUSCurrency(row.budgetTotalCost)}</Typography>
        </Box>
      ),
    },
    {
      field: "actualTotalCost",
      minWidth: 200,
      headerName: "Actual Total Cost",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography color="text.primary">{formatUSCurrency(row.actualTotalCost)}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "costVariance",
      minWidth: 180,
      headerName: "Cost Variance",
      renderCell: ({ row }) => {
        const value = typeof row.costVariance === 'number' && !isNaN(row.costVariance) ? row.costVariance : 0;
        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography color="text.primary">{formatUSCurrency(value)}</Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "costVariancePercentage",
      minWidth: 180,
      headerName: "Cost Variance %",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography color="text.primary">{row.costVariancePercentage}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "budgetedPrice",
      minWidth: 220,
      headerName: "Budgeted Total Price",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography color="text.primary">{formatUSCurrency(row.budgetedPrice)}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "actualPrice",
      minWidth: 220,
      headerName: "Actual Total Price",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography color="text.primary">{formatUSCurrency(row.actualPrice)}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "priceVariance",
      minWidth: 200,
      headerName: "Price Variance",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography color="text.primary">{formatUSCurrency(row.priceVariance)}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "priceVariancePercentage",
      minWidth: 200,
      headerName: "Price Variance %",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography color="text.primary">{row.priceVariancePercentage}</Typography>
        </Box>
      ),
    },
  ];
  //#endregion Other Cost
  
  //#region Subcontractor
  const columnsSubcontractor = [
    {
      flex: 0.1,
      field: "skillType",
      minWidth: 160,
      headerName: "Skill Type",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{row.skillType}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "employeeName",
      minWidth: 200,
      headerName: "Subcontractor",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{row.employeeName}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "unitName",
      minWidth: 200,
      headerName: "Unit of Measure",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{row.unitName || ''}{row.unitSymbol ? ` (${row.unitSymbol})` : ''}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "budgetedUnitQuantity",
      minWidth: 160,
      headerName: "Unit Quantity",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{row.budgetedUnitQuantity}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "budgetedCostPerUnit",
      minWidth: 180,
      headerName: "Cost Per Unit",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{formatUSCurrency(row.budgetedCostPerUnit)}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "budgetedTotalCost",
      minWidth: 140,
      headerName: "Total Cost",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{formatUSCurrency(row.budgetedTotalCost)}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "budgetedPricePerUnit",
      minWidth: 160,
      headerName: "Price Per Unit",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{formatUSCurrency(row.budgetedPricePerUnit)}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "budgetedTotalPrice",
      minWidth: 140,
      headerName: "Total Price",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{formatUSCurrency(row.budgetedTotalPrice)}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "budgetedMarkup",
      minWidth: 140,
      headerName: "Markup %",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{PercentageFormat(row.budgetedMarkup)}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "budgetedProfitPerUnit",
      minWidth: 160,
      headerName: "Profit Per Unit",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{formatUSCurrency(row.budgetedProfitPerUnit)}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "budgetedTotalProfit",
      minWidth: 140,
      headerName: "Total Profit",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{formatUSCurrency(row.budgetedTotalProfit)}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "providedByName",
      minWidth: 160,
      headerName: "Provided By",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{row.providedByName}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: 'actions',
      headerName: 'Actions',
      minWidth: 120,
      maxWidth: 120,
      type: 'actions',
      getActions: ({ id , row}) => disableEdit ? [] : [        
          <GridActionsCellItem
            sx={{ transform: 'scale(1.5)', marginRight:3 }}  
            icon={<Icon icon="tabler:pencil" />}
            label="Edit"
            onClick={() =>  handleJobSubcontractorEdit(row)}
          />,       
          <GridActionsCellItem
            color="error"
            sx={{ transform: 'scale(1.5)' }}  
            icon={<Icon icon="tabler:trash" />}
            label="Delete"
            onClick={() =>  handleJobSubcontractorDelete(id)}
          />
      ],
    },
  ];

  const columnsActualSubContractor = [
    {
      flex: 0.1,
      field: "skillType",
      minWidth: 160,
      headerName: "Skill Type",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{row.skillType}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "employeeName",
      minWidth: 200,
      headerName: "Subcontractor",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{row.employeeName}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "unitID",
      minWidth: 200,
      headerName: "Unit of Measure",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{row.unitName}{row.unitSymbol ? ` (${row.unitSymbol})` : ''}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "actualUnitQuantity",
      minWidth: 160,
      headerName: "Actual Unit Quantity",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{row.actualUnitQuantity}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "actualCostPerUnit",
      minWidth: 180,
      headerName: "Actual Cost Per Unit",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{formatUSCurrency(row.actualCostPerUnit)}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "actualTotalCost",
      minWidth: 140,
      headerName: "Actual Total Cost",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{formatUSCurrency(row.actualTotalCost)}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "actualPricePerUnit",
      minWidth: 160,
      headerName: "Actual Price Per Unit",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{formatUSCurrency(row.actualPricePerUnit)}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "actualTotalPrice",
      minWidth: 140,
      headerName: "Actual Total Price",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{formatUSCurrency(row.actualTotalPrice)}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "actualMarkup",
      minWidth: 140,
      headerName: "Actual Markup %",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{PercentageFormat(row.actualMarkup)}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "actualProfitPerUnit",
      minWidth: 160,
      headerName: "Actual Profit Per Unit",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{formatUSCurrency(row.actualProfitPerUnit)}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "actualTotalProfit",
      minWidth: 140,
      headerName: "Actual Total Profit",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{formatUSCurrency(row.actualTotalProfit)}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "providedByName",
      minWidth: 160,
      headerName: "Provided By",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{row.providedByName}</Typography>
        </Box>
      ),
    },
     {
      flex: 0.1,
      field: "modifiedBy",
      minWidth: 180,
      headerName: "Modified By",
      renderCell: ({ row }) => {
        const { modifiedbyname } = row;
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Typography>{modifiedbyname}</Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: 'actions',
      headerName: 'Actions',
      minWidth: 120,
      maxWidth: 120,
      type: 'actions',
      getActions: ({ id , row}) => disableEdit ? [] : [        
          <GridActionsCellItem
            sx={{ transform: 'scale(1.5)', marginRight:3 }}  
            icon={<Icon icon="tabler:pencil" />}
            label="Edit"
            onClick={() =>  handleJobSubcontractorEdit(row)}
          />,       
          <GridActionsCellItem
            color="error"
            sx={{ transform: 'scale(1.5)' }}  
            icon={<Icon icon="tabler:trash" />}
            label="Delete"
            onClick={() =>  handleJobSubcontractorDelete(id)}
          />
      ],
    },
  ];

  const columnsActualvsBudgetSubContractor = [
    {
      field: "employeeName",
      minWidth: 200,
      headerName: "Subcontractor",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{row.employeeName}</Typography>
        </Box>
      ),
    },
    {
      field: "skillType",
      minWidth: 140,
      headerName: "Skill Type",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{row.skillType}</Typography>
        </Box>
      ),
    },
    {
      field: "unitID",
      minWidth: 140,
      headerName: "Unit of Measure",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{row.unitName || ''}{row.unitSymbol ? ` (${row.unitSymbol})` : ''}</Typography>
        </Box>
      ),
    },
    {
      field: "budgetedUnitQuantity",
      minWidth: 180,
      headerName: "Budgeted Unit Quantity",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{row.budgetedUnitQuantity}</Typography>
        </Box>
      ),
    },
    {
      field: "actualUnitQuantity",
      minWidth: 160,
      headerName: "Actual Unit Quantity",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{row.actualUnitQuantity}</Typography>
        </Box>
      ),
    },
    {
      field: "budgetedCostPerUnit",
      minWidth: 220,
      headerName: "Budgeted Cost / Unit",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{formatUSCurrency(row.budgetedCostPerUnit)}</Typography>
        </Box>
      ),
    },
    {
      field: "actualCostPerUnit",
      minWidth: 200,
      headerName: "Actual Cost / Unit",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{formatUSCurrency(row.actualCostPerUnit)}</Typography>
        </Box>
      ),
    },
    {
      field: "budgetedTotalCost",
      minWidth: 220,
      headerName: "Budgeted Total Cost",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography color="text.primary">{formatUSCurrency(row.budgetedTotalCost)}</Typography>
        </Box>
      ),
    },
    {
      field: "actualTotalCost",
      minWidth: 200,
      headerName: "Actual Total Cost",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography color="text.primary">{formatUSCurrency(row.actualTotalCost)}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "costVariance",
      minWidth: 180,
      headerName: "Cost Variance",
      renderCell: ({ row }) => {
        const value = typeof row.costVariance === 'number' && !isNaN(row.costVariance) ? row.costVariance : 0;
        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography color="text.primary">{formatUSCurrency(value)}</Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "costVariancePercentage",
      minWidth: 180,
      headerName: "Cost Variance %",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography color="text.primary">{row.costVariancePercentage}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "budgetedTotalPrice",
      minWidth: 220,
      headerName: "Budgeted Total Price",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography color="text.primary">{formatUSCurrency(row.budgetedTotalPrice)}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "actualTotalPrice",
      minWidth: 220,
      headerName: "Actual Total Price",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography color="text.primary">{formatUSCurrency(row.actualTotalPrice)}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "priceVariance",
      minWidth: 200,
      headerName: "Price Variance",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography color="text.primary">{formatUSCurrency(row.priceVariance)}</Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      field: "priceVariancePercentage",
      minWidth: 200,
      headerName: "Price Variance %",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography color="text.primary">{row.priceVariancePercentage}</Typography>
        </Box>
      ),
    },
  ];
  //#endregion Subcontractor

  return({
    columnsMaterial,
    columnsActualMaterial,
    columnsActualvsBudgetMaterial,
    columnsLabor,
    columnsActualLabor,
    columnsActualvsBudgetLabor,
    columnsSubcontractor,
    columnsAssets,
    columnsAssetsDetail,
    columnsActualAssets,
    columnsActualvsBudgetAssets,
    columnsOtherCost,
    columnsActualOtherCost,
    columnsActualvsBudgetOtherCost,
    columnsActualSubContractor,
    columnsActualvsBudgetSubContractor,    
  });
} 
